/**
 * HangarOverlay.js
 *
 * June 2026 — Hangar / Shipyard.
 *
 * Reachable from the Shipyard terminal inside a docked space-station interior.
 * Lists all 6 procedurally-built ships, lets the player buy any they can afford
 * and select one they own as their active ship. Selecting rebuilds the 3D scene
 * with the new hull + applies its stats (speed / cargo / hull-shield).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Rocket, Gauge, Package, Shield, Lock, Check, DoorOpen, Wrench } from 'lucide-react';

function StatBar({ icon: Icon, label, value, max, tone }) {
  const pct = Math.max(6, Math.min(100, Math.round((value / max) * 100)));
  return (
    <div className="flex items-center gap-2" data-testid={`ship-stat-${label.toLowerCase()}`}>
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="text-[10px] uppercase tracking-wider text-slate-400 w-12 shrink-0">{label}</span>
      <div className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] tabular-nums text-slate-300 w-9 text-right">{value}</span>
    </div>
  );
}

export default function HangarOverlay({
  open,
  api,
  credits,
  onClose,
  onSelected,     // (shipId, shipDef) => void   — parent rebuilds the scene
  onOpenModules,  // () => void                  — opens the Outfitting / modules overlay
  updateCredits,
}) {
  const [ships, setShips] = useState([]);
  const [owned, setOwned] = useState(['scout']);
  const [active, setActive] = useState('scout');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/ships/garage');
      setShips(data?.ships || []);
      setOwned(data?.owned_ships || ['scout']);
      setActive(data?.active_ship || 'scout');
    } catch (_e) { /* noop */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const handleBuy = useCallback(async (ship) => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/ships/buy', { ship_id: ship.id });
      if (data?.credits != null) updateCredits?.(data.credits);
      setOwned(data?.owned_ships || owned);
      toast.success(`Purchased ${ship.name} · −${ship.price.toLocaleString()} cr`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Purchase failed');
    } finally { setBusy(false); }
  }, [api, owned, updateCredits]);

  const handleSelect = useCallback(async (ship) => {
    setBusy(true);
    try {
      await api.post('/api/ships/select', { ship_id: ship.id });
      setActive(ship.id);
      toast.success(`${ship.name} is now your active ship`);
      onSelected?.(ship.id, ship);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not switch ship');
    } finally { setBusy(false); }
  }, [api, onSelected]);

  const sorted = useMemo(
    () => [...ships].sort((a, b) => (a.price || 0) - (b.price || 0)),
    [ships],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="hangar-overlay"
    >
      <div
        className="w-full max-w-4xl max-h-[88vh] overflow-hidden rounded-2xl border border-fuchsia-400/25 bg-[#0c0a14]/95 shadow-[0_0_60px_rgba(190,90,255,0.18)] flex flex-col"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Rocket className="w-5 h-5 text-fuchsia-300" />
            <div>
              <h2 className="text-lg font-semibold text-fuchsia-100 tracking-wide">Shipyard · Hangar</h2>
              <p className="text-[11px] text-slate-400">Buy & switch your active spacecraft</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1.5 rounded-lg bg-amber-400/10 border border-amber-300/25 text-amber-200 text-sm tabular-nums" data-testid="hangar-credits">
              {Number(credits || 0).toLocaleString()} cr
            </div>
            {onOpenModules && (
              <Button
                variant="outline" size="sm" onClick={onOpenModules}
                data-testid="hangar-outfitting-button"
                className="border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10"
              >
                <Wrench className="w-4 h-4 mr-1" /> Outfitting
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="hangar-close-button" className="text-slate-300 hover:text-white">
              <DoorOpen className="w-4 h-4 mr-1" /> Close
            </Button>
          </div>
        </div>

        {/* Ship grid */}
        <div className="overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {loading && <div className="col-span-2 text-center text-slate-400 py-10">Loading hangar…</div>}
          {!loading && sorted.map((ship) => {
            const isOwned = owned.includes(ship.id);
            const isActive = active === ship.id;
            const canAfford = (credits || 0) >= ship.price;
            return (
              <div
                key={ship.id}
                className={`rounded-xl border p-4 flex flex-col gap-3 transition-colors ${
                  isActive ? 'border-fuchsia-400/60 bg-fuchsia-500/10' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.05]'
                }`}
                data-testid={`ship-card-${ship.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-100">{ship.name}</h3>
                      {isActive && <Badge className="bg-fuchsia-500/80 text-white text-[10px]">Active</Badge>}
                      {ship.has_interior && (
                        <Badge variant="outline" className="text-[10px] border-cyan-400/40 text-cyan-200">Walkable</Badge>
                      )}
                    </div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-500">{ship.klass}</p>
                  </div>
                  <div className="text-right">
                    {ship.price > 0
                      ? <span className="text-sm tabular-nums text-amber-200">{ship.price.toLocaleString()} cr</span>
                      : <span className="text-xs text-emerald-300">Starter</span>}
                  </div>
                </div>

                <p className="text-xs text-slate-400 leading-relaxed min-h-[48px]">{ship.description}</p>

                <div className="space-y-1.5">
                  <StatBar icon={Gauge} label="Speed" value={Math.round(ship.speed_mult * 100)} max={150} tone="bg-cyan-400" />
                  <StatBar icon={Package} label="Cargo" value={ship.cargo} max={160} tone="bg-emerald-400" />
                  <StatBar icon={Shield} label="Hull" value={ship.hull + ship.shield} max={330} tone="bg-amber-400" />
                </div>

                <div className="mt-1">
                  {isActive ? (
                    <Button disabled className="w-full bg-fuchsia-500/30 text-fuchsia-100" data-testid={`ship-active-${ship.id}`}>
                      <Check className="w-4 h-4 mr-1" /> Active Ship
                    </Button>
                  ) : isOwned ? (
                    <Button
                      onClick={() => handleSelect(ship)}
                      disabled={busy}
                      className="w-full bg-cyan-500/90 hover:bg-cyan-400 text-slate-900 font-semibold"
                      data-testid={`ship-select-${ship.id}`}
                    >
                      Fly This Ship
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleBuy(ship)}
                      disabled={busy || !canAfford}
                      className={`w-full font-semibold ${canAfford ? 'bg-amber-400/90 hover:bg-amber-300 text-slate-900' : 'bg-white/5 text-slate-500'}`}
                      data-testid={`ship-buy-${ship.id}`}
                    >
                      {canAfford ? <>Buy · {ship.price.toLocaleString()} cr</> : <><Lock className="w-3.5 h-3.5 mr-1" /> Need {ship.price.toLocaleString()} cr</>}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
